import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Mail } from "lucide-react";
import { toast } from "sonner";
import { newsletterSignup } from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await newsletterSignup(email);
      toast.success("Welcome to the BananaX circle.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-ink-900 mt-32 pt-24 pb-10 border-t border-white/5" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Brand + newsletter */}
        <div className="md:col-span-5 space-y-6">
          <Link to="/" className="font-serif text-3xl tracking-tight text-bone">
            Banana<span className="text-gold italic">X</span>
            <span className="text-gold text-base align-top">&trade;</span>
          </Link>
          <p className="label-eyebrow text-gold/80">Refined by Nature</p>
          <p className="text-mist text-sm max-w-md leading-relaxed font-light">
            Premium intimate wellness, engineered for confidence and crafted for discretion.
            Each piece, electronically tested. Each box, quietly shipped.
          </p>
          <form onSubmit={subscribe} className="flex items-center max-w-md border-b border-white/15 pb-2" data-testid="newsletter-form">
            <Mail className="w-4 h-4 text-mist stroke-[1.4] mr-3" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent text-sm font-light text-bone placeholder:text-mist/60 outline-none py-2"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="text-[11px] tracking-[0.2em] uppercase text-gold hover:text-gold-soft"
              data-testid="newsletter-submit-btn"
            >
              {loading ? "..." : "Join"}
            </button>
          </form>
          <div className="flex gap-5 pt-2">
            <a href="#" className="text-mist hover:text-gold" aria-label="Instagram">
              <Instagram className="w-4 h-4 stroke-[1.4]" />
            </a>
            <a href="#" className="text-mist hover:text-gold" aria-label="Twitter">
              <Twitter className="w-4 h-4 stroke-[1.4]" />
            </a>
          </div>
        </div>

        {/* Columns */}
        <div className="md:col-span-2 space-y-4">
          <h4 className="label-eyebrow">Shop</h4>
          <ul className="space-y-2 text-sm text-mist font-light">
            <li><Link to="/shop" className="hover:text-gold">All Products</Link></li>
            <li><Link to="/shop?category=ultra-thin" className="hover:text-gold">Ultra Thin</Link></li>
            <li><Link to="/shop?category=dotted-ribbed" className="hover:text-gold">Dotted & Ribbed</Link></li>
            <li><Link to="/shop?category=long-lasting" className="hover:text-gold">Long Lasting</Link></li>
            <li><Link to="/shop?category=flavored" className="hover:text-gold">Flavored</Link></li>
            <li><Link to="/shop?category=combo" className="hover:text-gold">Combo Packs</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2 space-y-4">
          <h4 className="label-eyebrow">Brand</h4>
          <ul className="space-y-2 text-sm text-mist font-light">
            <li><Link to="/about" className="hover:text-gold">Our Story</Link></li>
            <li><Link to="/campaigns" className="hover:text-gold">Campaigns</Link></li>
            <li><Link to="/bundle" className="hover:text-gold">Bundle Builder</Link></li>
            <li><Link to="/subscription" className="hover:text-gold">Subscription</Link></li>
            <li><Link to="/blog" className="hover:text-gold">Journal</Link></li>
            <li><Link to="/quiz" className="hover:text-gold">Product Finder</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3 space-y-4">
          <h4 className="label-eyebrow">Support</h4>
          <ul className="space-y-2 text-sm text-mist font-light">
            <li><Link to="/track" className="hover:text-gold">Track Order</Link></li>
            <li><Link to="/contact" className="hover:text-gold">Contact Concierge</Link></li>
            <li><Link to="/policy/shipping" className="hover:text-gold">Shipping Policy</Link></li>
            <li><Link to="/policy/privacy" className="hover:text-gold">Privacy Policy</Link></li>
            <li><Link to="/policy/terms" className="hover:text-gold">Terms & Conditions</Link></li>
          </ul>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 mt-16">
        <div className="gold-divider mb-8"></div>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-mist text-xs font-light">
            © {new Date().getFullYear()} BananaX&trade;. All rights reserved.
          </p>
          <p className="font-serif text-lg text-bone">
            Be Ready. <span className="text-gold">Stay Protected.</span>
          </p>
          <p className="label-eyebrow">For adults 18+ only · Discreet shipping</p>
        </div>
      </div>
    </footer>
  );
}
